import { useMemo } from "react";
import { useWindowViewport } from "./useWindowViewport";

type Layout = {
  stageStyle: React.CSSProperties;
  pipStyle: React.CSSProperties;
  pipInnerStyle: React.CSSProperties;
  isPortrait: boolean;
  isMobile: boolean;
};

type Options = {
  footerHeightPx?: number;
  gapPx?: number;
  pipWidthDesktop?: number;
  pipHeightDesktop?: number;
  pipRotationDeg?: 0 | 90 | 180 | 270;
  stageRotationDeg?: 0 | 90 | 180 | 270;
  fitMode?: "contain" | "cover";
};

export function useVideoLayoutManager(opts: Options = {}): Layout {
  const vp = useWindowViewport();

  const footerH = opts.footerHeightPx ?? 64;
  const gap = opts.gapPx ?? 10;

  const usableW = vp.layoutWidth;
  const usableH = Math.max(0, vp.layoutHeight);

  const isMobile = usableW <= 768;
  const isPortrait = vp.isPortrait;

  const safeBottom = footerH + gap;
  const safeTop = gap;
  const safeLeft = gap;
  const safeRight = gap;

  const stageStyle = useMemo<React.CSSProperties>(() => {
    return {
      position: "absolute",
      inset: 0,
      width: usableW,
      height: usableH,
      overflow: "hidden",
      background: "black",
    };
  }, [usableW, usableH]);

  const { pipW, pipH } = useMemo(() => {
    const desktopW = opts.pipWidthDesktop ?? 240;
    const desktopH = opts.pipHeightDesktop ?? 160;

    if (!isMobile) {
      return { pipW: desktopW, pipH: desktopH };
    }

    if (isPortrait) {
      const portraitAspect = 0.72;

      // ~10% smaller
      const sizeScale = 0.9;

      const widthByScreen = Math.floor(usableW * 0.42 * sizeScale);
      const maxHeightByScreen = Math.floor((usableH - safeTop - safeBottom) * 0.34 * sizeScale);

      let w = Math.min(172, Math.max(135, widthByScreen));
      let h = Math.floor(w / portraitAspect);

      if (h > maxHeightByScreen) {
        h = maxHeightByScreen;
        w = Math.floor(h * portraitAspect);
      }

      return { pipW: w, pipH: h };
    }

    // Landscape mobile
    const landscapeAspect = 1.2;

    // ~10% smaller
    const sizeScale = 0.9;

    const widthByScreen = Math.floor(usableW * 0.28 * sizeScale);
    const maxHeightByScreen = Math.floor((usableH - safeTop - safeBottom) * 0.34 * sizeScale);

    let w = Math.min(162, Math.max(120, widthByScreen));
    let h = Math.floor(w / landscapeAspect);

    if (h > maxHeightByScreen) {
      h = maxHeightByScreen;
      w = Math.floor(h * landscapeAspect);
    }

    return { pipW: w, pipH: h };
  }, [
    isMobile,
    isPortrait,
    usableW,
    usableH,
    safeTop,
    safeBottom,
    opts.pipWidthDesktop,
    opts.pipHeightDesktop,
  ]);

  const pipStyle = useMemo<React.CSSProperties>(() => {
    const maxW = Math.max(0, usableW - safeLeft - safeRight);
    const maxH = Math.max(0, usableH - safeTop - safeBottom);

    const width = Math.min(pipW, maxW);
    const height = Math.min(pipH, maxH);

    return {
      position: "absolute",
      right: safeRight,
      bottom: safeBottom,
      width,
      height,
      borderRadius: 12,
      overflow: "hidden",
      background: "transparent",
      zIndex: 5,
      boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
      touchAction: "none",
    };
  }, [usableW, usableH, pipW, pipH, safeLeft, safeRight, safeTop, safeBottom]);

  const pipInnerStyle = useMemo<React.CSSProperties>(() => {
    const rot = opts.pipRotationDeg ?? 0;
    const fit = isMobile ? "cover" : (opts.fitMode ?? "contain");

    const base: React.CSSProperties = {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: fit as any,
      display: "block",
      overflow: "hidden",
      background: "transparent",
    };

    if (rot === 0) return base;

    return {
      ...base,
      transform: `rotate(${rot}deg)`,
      transformOrigin: "center center",
    };
  }, [isMobile, opts.fitMode, opts.pipRotationDeg]);

  return { stageStyle, pipStyle, pipInnerStyle, isPortrait, isMobile };
}